<template>
  <div class="resultsScreen">

    <!-- BACKGROUND DECORATIVE DOTS -->
    <div v-for="n in 16" :key="n" class="confetti-dot"></div>

    <!-- HOME BUTTON -->
    <div class="homeBtn" @click="goToHomePage">
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" class="bi bi-house-fill" viewBox="0 0 16 16">
        <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L8 2.207l6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.707 1.5Z"/>
        <path d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6Z"/>
      </svg>
    </div>

    <!-- TITLE -->
    <div class="resultsTitle">{{ translation.results }}</div>

    <!-- RESULTS LIST -->
    <div class="resultsList">
      <div
        v-for="result in allResults"
        :key="result.id"
        class="resultCard"
        :class="{ gold: result.place === 1, silver: result.place === 2, bronze: result.place === 3 }"
      >
        <div class="resultPlace">
          <span v-if="result.place === 1">🥇</span>
          <span v-else-if="result.place === 2">🥈</span>
          <span v-else-if="result.place === 3">🥉</span>
          <span v-else class="placeNum">{{ result.place }}</span>
        </div>
        <div class="resultInfo">
          <div class="resultUserName">{{ result.resultsOfTheGame.userName }}</div>
          <div class="resultScore">
            <span class="scoreNum">{{ result.resultsOfTheGame.errorNumber }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#ffc44d" class="bi bi-star-fill" viewBox="0 0 16 16">
              <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- RATING POPUP (appears first, before all other popups) -->
    <transition name="fade">
      <div v-if="showRatingPopup" class="ratingOverlay">
        <div class="ratingPopCard">
          <div class="ratingTitle">{{ translation.howWasIt }}</div>
          <div class="starRow">
            <span
              v-for="n in 5"
              :key="n"
              @click="rating = n"
              class="ratingStar"
              :class="{ active: n <= rating }"
            >★</span>
          </div>
          <textarea
            v-model="comment"
            :placeholder="translation.leaveMessage"
            rows="3"
            class="ratingTextarea"
          ></textarea>
          <button class="ratingBtn" @click="sendComment">{{ translation.send }}</button>
          <div class="ratingSkip" @click="dismissRating">{{ translation.noThanks }}</div>
        </div>
      </div>
    </transition>

    <!-- WHATSAPP BOTTOM SHEET -->
    <transition name="sheet-slide">
      <div v-if="showWhatsappSheet" class="bottomSheet">
        <div class="sheetHandle"></div>
        <div class="sheetEmoji">📱</div>
        <div class="sheetTitle">{{ translation.whatsappTitle }}</div>
        <div class="sheetSubtitle">{{ translation.whatsappSubtitle }}</div>
        <button class="whatsappBtn" @click="openWhatsapp">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16" style="flex-shrink:0">
            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
          </svg>
          {{ translation.whatsappBtn }}
        </button>
        <div class="sheetSkip" @click="dismissWhatsapp">{{ translation.noThanks }}</div>
      </div>
    </transition>

    <!-- EMAIL BOTTOM SHEET -->
    <transition name="sheet-slide">
      <div v-if="showEmailSheet" class="bottomSheet">
        <div class="sheetHandle"></div>
        <div class="sheetEmoji">📬</div>
        <div class="sheetTitle">{{ translation.emailTitle }}</div>
        <div class="sheetSubtitle">{{ translation.emailSubtitle }}</div>
        <div class="emailRow">
          <input
            type="email"
            v-model="email"
            :placeholder="translation.emailPlaceholder"
            class="emailInput"
          />
          <button class="emailSendBtn" @click="sendEmail">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z"/>
            </svg>
          </button>
        </div>
        <div class="emailSuccess" v-if="emailSent">✅ {{ translation.emailSent }}</div>
        <div class="sheetSkip" @click="showEmailSheet = false">{{ translation.noThanks }}</div>
      </div>
    </transition>

  </div>
</template>

<script>
/* eslint-disable */
export default {
  data() {
    return {
      comment: "",
      rating: 5,
      showRatingPopup: true,
      email: "",
      emailSent: false,
      showWhatsappSheet: false,
      showEmailSheet: false,
      translation: {
        results: "לוח תוצאות",
        howWasIt: "איך היה לכם? 😊",
        leaveMessage: "כמה מילים על החוויה...",
        send: "שלח",
        noThanks: "לא תודה",
        thankYou: "תודה על המשוב!",
        whatsappTitle: "נהנתם? שתפו חברים! 🎉",
        whatsappSubtitle: "המליצו על המסלול הזה לחברים שלכם דרך וואטסאפ",
        whatsappBtn: "שתף בוואטסאפ",
        emailTitle: "קבלו מסלולים חדשים ראשונים 📬",
        emailSubtitle: "השאירו את הכתובת מייל שלכם ונודיע לכם כשייפתחו מסלולים חדשים",
        emailPlaceholder: "כתובת מייל",
        emailSent: "נרשמת בהצלחה!",
        backToHomePage: "דף הבית",
      },
      message: "",
    };
  },
  mounted() {
    history.pushState(null, null, location.href);
    window.onpopstate = function () { history.go(1); };
    this.$store.dispatch("getResultsByPoints", localStorage.getItem("navigationName"));
    this.translate(this.$store.getters.lang);
    this.message = this.buildWhatsappMessage(this.$store.getters.lang);
  },
  computed: {
    lang() { return this.translate(this.$store.getters.lang); },
    gameParameters() { return this.$store.getters.gameParameters; },
    allResults() { return this.$store.getters.results; },
    user() { return this.$store.getters.user; },
  },
  methods: {
    buildWhatsappMessage(lang) {
      const url = "https://eifoany.web.app/" + localStorage.getItem("navigationName");
      switch (lang) {
        case "fr": return "Je viens de terminer un super jeu de navigation ! Venez jouer ici\u00a0: " + url;
        case "en": return "I just finished an amazing navigation game! Come play here: " + url;
        case "ar": return "انتهيت للتو من لعبة ملاحة رائعة! العب هنا: " + url;
        case "de": return "Ich habe gerade ein tolles Navigationsspiel beendet! Spiel hier: " + url;
        default:   return "כרגע סיימתי משחק ניווט מומלץ. אפשר לשחק בו בקישור הזה: " + url;
      }
    },
    sendComment() {
      this.$store.dispatch("sendComment", {
        navigationName: localStorage.getItem("navigationName"),
        rating: this.rating,
        comment: this.comment,
        time: new Date().toString(),
        user: localStorage.getItem("myNameOuchui"),
      });
      this.dismissRating();
    },
    dismissRating() {
      this.showRatingPopup = false;
      // WhatsApp sheet 6s after the rating popup is closed
      setTimeout(() => { this.showWhatsappSheet = true; }, 6000);
    },
    dismissWhatsapp() {
      this.showWhatsappSheet = false;
      // Email sheet after 8s
      setTimeout(() => {
        this.showEmailSheet = true;
      }, 8000);
    },
    openWhatsapp() {
      window.open("whatsapp://send?text=" + encodeURIComponent(this.message));
      this.dismissWhatsapp();
    },
    sendEmail() {
      if (!this.email) return;
      this.$store.dispatch("sendEmail", {
        navigationName: this.gameParameters ? this.gameParameters.navigationName : localStorage.getItem("navigationName"),
        email: this.email,
      });
      this.email = "";
      this.emailSent = true;
      setTimeout(() => { this.showEmailSheet = false; }, 2000);
    },
    goToHomePage() {
      this.$router.push("/" + localStorage.getItem("navigationName"));
      localStorage.clear();
    },
    translate(lang) {
      switch (lang) {
        case "fr":
          this.translation = {
            results: "Tableau des résultats",
            howWasIt: "Comment s'est passé le jeu\u00a0? 😊",
            leaveMessage: "Quelques mots sur votre expérience...",
            send: "Envoyer",
            noThanks: "Non merci",
            thankYou: "Merci pour votre avis\u00a0!",
            whatsappTitle: "Vous avez aimé\u00a0? Partagez\u00a0! 🎉",
            whatsappSubtitle: "Recommandez ce parcours à vos amis via WhatsApp",
            whatsappBtn: "Partager sur WhatsApp",
            emailTitle: "Recevez les nouveaux parcours 📬",
            emailSubtitle: "Laissez votre email pour être informé en avant-première des nouveaux parcours",
            emailPlaceholder: "Votre adresse email",
            emailSent: "Inscription réussie\u00a0!",
            backToHomePage: "Page d'accueil",
          };
          break;
        case "en":
          this.translation = {
            results: "Leaderboard",
            howWasIt: "How was the game? 😊",
            leaveMessage: "A few words about your experience...",
            send: "Send",
            noThanks: "No thanks",
            thankYou: "Thanks for your feedback!",
            whatsappTitle: "Loved it? Share it! 🎉",
            whatsappSubtitle: "Recommend this trail to your friends via WhatsApp",
            whatsappBtn: "Share on WhatsApp",
            emailTitle: "Get new trails first 📬",
            emailSubtitle: "Leave your email and we'll notify you when new trails open",
            emailPlaceholder: "Your email address",
            emailSent: "Successfully subscribed!",
            backToHomePage: "Home page",
          };
          break;
        case "ar":
          this.translation = {
            results: "لوحة النتائج",
            howWasIt: "كيف كانت اللعبة؟ 😊",
            leaveMessage: "بعض الكلمات عن تجربتك...",
            send: "إرسال",
            noThanks: "لا شكرا",
            thankYou: "شكرًا على رأيك!",
            whatsappTitle: "استمتعت؟ شارك الآن! 🎉",
            whatsappSubtitle: "أوصِ بهذا المسار لأصدقائك عبر واتساب",
            whatsappBtn: "مشاركة عبر واتساب",
            emailTitle: "احصل على المسارات الجديدة أولاً 📬",
            emailSubtitle: "اترك بريدك الإلكتروني لنخبرك عند فتح مسارات جديدة",
            emailPlaceholder: "عنوان بريدك الإلكتروني",
            emailSent: "تم التسجيل بنجاح!",
            backToHomePage: "الصفحة الرئيسية",
          };
          break;
        case "de":
          this.translation = {
            results: "Rangliste",
            howWasIt: "Wie war das Spiel? 😊",
            leaveMessage: "Ein paar Worte zu Ihrem Erlebnis...",
            send: "Senden",
            noThanks: "Nein danke",
            thankYou: "Danke für Ihr Feedback!",
            whatsappTitle: "Hat es Spaß gemacht? Teilen! 🎉",
            whatsappSubtitle: "Empfehlen Sie diesen Weg Ihren Freunden über WhatsApp",
            whatsappBtn: "Auf WhatsApp teilen",
            emailTitle: "Neue Routen zuerst erhalten 📬",
            emailSubtitle: "Hinterlassen Sie Ihre E-Mail und wir informieren Sie bei neuen Routen",
            emailPlaceholder: "Ihre E-Mail-Adresse",
            emailSent: "Erfolgreich angemeldet!",
            backToHomePage: "Startseite",
          };
          break;
        default: // Hebrew
          this.translation = {
            results: "לוח תוצאות",
            howWasIt: "איך היה לכם? 😊",
            leaveMessage: "כמה מילים על החוויה...",
            send: "שלח",
            noThanks: "לא תודה",
            thankYou: "תודה על המשוב!",
            whatsappTitle: "נהנתם? שתפו חברים! 🎉",
            whatsappSubtitle: "המליצו על המסלול הזה לחברים שלכם דרך וואטסאפ",
            whatsappBtn: "שתף בוואטסאפ",
            emailTitle: "קבלו מסלולים חדשים ראשונים 📬",
            emailSubtitle: "השאירו את הכתובת מייל שלכם ונודיע לכם כשייפתחו מסלולים חדשים",
            emailPlaceholder: "כתובת מייל",
            emailSent: "נרשמת בהצלחה!",
            backToHomePage: "דף הבית",
          };
          break;
      }
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: VarelaRound;
  src: url(../../static/fonts/VarelaRound-Regular.ttf);
}

/* ===== FULL SCREEN BACKGROUND ===== */
.resultsScreen {
  position: fixed;
  top: 0; left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 590px;
  height: 100vh;
  z-index: 100;
  background: linear-gradient(160deg, #0d1b4b 0%, #1a3a8f 45%, #6b21a8 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 70px 16px 48px;
  font-family: VarelaRound, system-ui, sans-serif;
}

/* ===== DECORATIVE DOTS ===== */
.confetti-dot {
  position: fixed;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: rgba(255,255,255,0.12);
  pointer-events: none;
}
.confetti-dot:nth-child(odd)  { background: rgba(245,158,11,0.15); }
.confetti-dot:nth-child(1)  { top: 8%;  left: 12%; width: 10px; height: 10px; }
.confetti-dot:nth-child(2)  { top: 15%; left: 80%; width: 8px;  height: 8px;  }
.confetti-dot:nth-child(3)  { top: 30%; left: 5%;  }
.confetti-dot:nth-child(4)  { top: 45%; left: 90%; width: 12px; height: 12px; }
.confetti-dot:nth-child(5)  { top: 60%; left: 20%; }
.confetti-dot:nth-child(6)  { top: 70%; left: 75%; width: 9px;  height: 9px;  }
.confetti-dot:nth-child(7)  { top: 85%; left: 40%; }
.confetti-dot:nth-child(8)  { top: 5%;  left: 55%; width: 7px;  height: 7px;  }
.confetti-dot:nth-child(9)  { top: 20%; left: 35%; width: 5px;  height: 5px;  }
.confetti-dot:nth-child(10) { top: 50%; left: 60%; }
.confetti-dot:nth-child(11) { top: 75%; left: 8%;  width: 8px;  height: 8px;  }
.confetti-dot:nth-child(12) { top: 90%; left: 85%; }
.confetti-dot:nth-child(13) { top: 35%; left: 50%; }
.confetti-dot:nth-child(14) { top: 55%; left: 30%; width: 10px; height: 10px; }
.confetti-dot:nth-child(15) { top: 65%; left: 95%; }
.confetti-dot:nth-child(16) { top: 92%; left: 15%; width: 7px;  height: 7px;  }

/* ===== HOME BUTTON ===== */
.homeBtn {
  position: fixed;
  top: 72px; left: 16px;
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  backdrop-filter: blur(8px);
  z-index: 10;
  transition: background 0.15s;
}
.homeBtn:active { background: rgba(255,255,255,0.25); }

/* ===== TITLE ===== */
.resultsTitle {
  font-size: 32px;
  font-weight: 900;
  color: white;
  text-align: center;
  margin-bottom: 24px;
  letter-spacing: 1px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.3);
}

/* ===== RESULTS LIST ===== */
.resultsList {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.resultCard {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 18px;
  backdrop-filter: blur(10px);
  color: white;
  animation: cardSlideIn 0.4s ease-out both;
}
.resultCard.gold   { border-color: rgba(255,215,0,0.5);   background: rgba(255,215,0,0.1);  }
.resultCard.silver { border-color: rgba(192,192,192,0.4); background: rgba(192,192,192,0.07); }
.resultCard.bronze { border-color: rgba(205,127,50,0.4);  background: rgba(205,127,50,0.07); }

@keyframes cardSlideIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

.resultPlace { font-size: 36px; min-width: 44px; text-align: center; line-height: 1; }
.placeNum    { font-size: 22px; font-weight: 800; color: rgba(255,255,255,0.6); }

.resultInfo  { flex: 1; text-align: right; direction: rtl; }
.resultUserName { font-size: 20px; font-weight: 800; }
.resultScore {
  display: flex; align-items: center;
  justify-content: flex-end; gap: 6px; margin-top: 4px;
}
.scoreNum { font-size: 22px; font-weight: 900; color: #ffc44d; }

/* ===== RATING POPUP ===== */
.ratingOverlay {
  position: fixed;
  top: 0; left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 590px;
  height: 100vh;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(6px);
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ratingPopCard {
  background: linear-gradient(160deg, #1a2f7a 0%, #2d1b69 100%);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 28px;
  padding: 32px 24px;
  width: 88%;
  max-width: 360px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}

.ratingTitle {
  font-size: 22px;
  font-weight: 900;
  text-align: center;
}

.starRow { display: flex; gap: 8px; }

.ratingStar {
  font-size: 42px;
  cursor: pointer;
  color: rgba(255,255,255,0.2);
  transition: color 0.15s, transform 0.1s;
  line-height: 1;
}
.ratingStar.active { color: #ffc44d; }
.ratingStar:active { transform: scale(1.2); }

.ratingTextarea {
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.08);
  color: white;
  font-size: 15px;
  padding: 12px 14px;
  resize: none;
  font-family: VarelaRound, system-ui, sans-serif;
}
.ratingTextarea::placeholder { color: rgba(255,255,255,0.45); }

.ratingBtn {
  width: 100%;
  background: white;
  color: #1a3a8f;
  font-weight: 800;
  font-size: 17px;
  border: none;
  border-radius: 50px;
  padding: 14px 0;
  cursor: pointer;
  font-family: VarelaRound, system-ui, sans-serif;
  transition: transform 0.1s;
  box-shadow: 0 4px 16px rgba(255,255,255,0.2);
}
.ratingBtn:active { transform: scale(0.97); }

.ratingSkip {
  font-size: 13px;
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  text-decoration: underline;
}

/* fade transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ===== BOTTOM SHEETS ===== */
.bottomSheet {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 590px;
  background: linear-gradient(180deg, #1a2f7a 0%, #0d1b4b 100%);
  border-top: 1px solid rgba(255,255,255,0.2);
  border-radius: 28px 28px 0 0;
  padding: 12px 24px 40px;
  z-index: 300;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 -8px 40px rgba(0,0,0,0.5);
  color: white;
}

.sheetHandle {
  width: 40px;
  height: 4px;
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
  margin-bottom: 4px;
}

.sheetEmoji { font-size: 44px; line-height: 1; }

.sheetTitle {
  font-size: 20px;
  font-weight: 900;
  text-align: center;
}

.sheetSubtitle {
  font-size: 14px;
  color: rgba(255,255,255,0.75);
  text-align: center;
  line-height: 1.5;
  max-width: 300px;
}

/* WhatsApp button */
.whatsappBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  background: #25D366;
  color: white;
  font-weight: 800;
  font-size: 17px;
  border: none;
  border-radius: 50px;
  padding: 15px 0;
  cursor: pointer;
  font-family: VarelaRound, system-ui, sans-serif;
  box-shadow: 0 4px 16px rgba(37,211,102,0.4);
  transition: transform 0.1s, box-shadow 0.1s;
}
.whatsappBtn:active {
  transform: scale(0.97);
  box-shadow: 0 2px 8px rgba(37,211,102,0.3);
}

/* Email row */
.emailRow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.emailInput {
  flex: 1;
  height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.12);
  color: white;
  font-size: 15px;
  padding: 0 14px;
  font-family: VarelaRound, system-ui, sans-serif;
}
.emailInput::placeholder { color: rgba(255,255,255,0.5); }

.emailSendBtn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #0090e3;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0,144,227,0.4);
  transition: transform 0.1s;
}
.emailSendBtn:active { transform: scale(0.95); }

.emailSuccess {
  font-size: 14px;
  color: #7effc5;
  text-align: center;
  font-weight: 600;
}

.sheetSkip {
  font-size: 13px;
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  text-decoration: underline;
  margin-top: 4px;
}

/* ===== SHEET SLIDE TRANSITION ===== */
.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1);
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateX(-50%) translateY(100%);
}
.sheet-slide-enter-to,
.sheet-slide-leave-from {
  transform: translateX(-50%) translateY(0);
}
</style>
